import { useState, useCallback, useRef, useEffect } from 'react';
import { CardData, Action, PostIt } from '../types';

// Tipo para um snapshot do estado
export interface HistorySnapshot {
  cards: CardData[];
  actions: Action[];
  postIts: PostIt[];
  timestamp: number;
  description?: string;
}

// Configurações do histórico
const MAX_HISTORY_SIZE = 50; // Máximo de estados no histórico
const DEBOUNCE_TIME = 500; // ms para agrupar mudanças rápidas

export function useHistory(
  currentCards: CardData[],
  currentActions: Action[],
  currentPostIts: PostIt[],
  setCards: (cards: CardData[]) => void,
  setActions: (actions: Action[]) => void,
  setPostIts: (postIts: PostIt[]) => void
) {
  // Pilhas de histórico
  const [undoStack, setUndoStack] = useState<HistorySnapshot[]>([]);
  const [redoStack, setRedoStack] = useState<HistorySnapshot[]>([]);
  
  // Referência para debounce
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastSnapshotRef = useRef<HistorySnapshot | null>(null);
  const isRestoringRef = useRef(false);
  
  // Criar snapshot do estado atual
  const createSnapshot = useCallback((description?: string): HistorySnapshot => {
    return {
      cards: JSON.parse(JSON.stringify(currentCards)),
      actions: JSON.parse(JSON.stringify(currentActions)),
      postIts: JSON.parse(JSON.stringify(currentPostIts)),
      timestamp: Date.now(),
      description
    };
  }, [currentCards, currentActions, currentPostIts]);
  
  // Verificar se dois snapshots são iguais (para evitar duplicatas)
  const areSnapshotsEqual = useCallback((a: HistorySnapshot | null, b: HistorySnapshot): boolean => {
    if (!a) return false;
    
    // Comparar arrays de forma simples (pelo stringify)
    const cardsEqual = JSON.stringify(a.cards) === JSON.stringify(b.cards);
    const actionsEqual = JSON.stringify(a.actions) === JSON.stringify(b.actions);
    const postItsEqual = JSON.stringify(a.postIts) === JSON.stringify(b.postIts);
    
    return cardsEqual && actionsEqual && postItsEqual;
  }, []);
  
  // Salvar estado no histórico (com debounce)
  const pushToHistory = useCallback((description?: string) => {
    // Ignorar se estamos restaurando um estado
    if (isRestoringRef.current) return;
    
    // Limpar timer anterior
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    debounceTimerRef.current = setTimeout(() => {
      const newSnapshot = createSnapshot(description);
      
      // Evitar duplicatas
      if (areSnapshotsEqual(lastSnapshotRef.current, newSnapshot)) {
        return;
      }
      
      setUndoStack(prev => {
        const newStack = [...prev, newSnapshot].slice(-MAX_HISTORY_SIZE);
        return newStack;
      });
      
      // Limpar redo stack quando nova ação é realizada
      setRedoStack([]);
      
      lastSnapshotRef.current = newSnapshot;
    }, DEBOUNCE_TIME);
  }, [createSnapshot, areSnapshotsEqual]);
  
  // Salvar estado imediatamente (sem debounce) - para ações importantes
  const pushToHistoryImmediate = useCallback((description?: string) => {
    if (isRestoringRef.current) return;
    
    // Limpar qualquer debounce pendente
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    
    const newSnapshot = createSnapshot(description);
    
    // Evitar duplicatas
    if (areSnapshotsEqual(lastSnapshotRef.current, newSnapshot)) {
      return;
    }
    
    setUndoStack(prev => {
      const newStack = [...prev, newSnapshot].slice(-MAX_HISTORY_SIZE);
      return newStack;
    });
    
    setRedoStack([]);
    lastSnapshotRef.current = newSnapshot;
  }, [createSnapshot, areSnapshotsEqual]);
  
  // Desfazer
  const undo = useCallback(() => {
    if (undoStack.length === 0) {
      return false;
    }
    
    isRestoringRef.current = true;
    
    // Salvar estado atual no redo antes de desfazer
    const currentSnapshot = createSnapshot('Redo point');
    setRedoStack(prev => [...prev, currentSnapshot]);
    
    // Pegar último estado do undo stack
    const previousState = undoStack[undoStack.length - 1];
    setUndoStack(prev => prev.slice(0, -1));
    
    // Restaurar estado
    console.log('[History] Desfazendo:', previousState.description || 'Ação anterior');
    setCards(previousState.cards);
    setActions(previousState.actions);
    setPostIts(previousState.postIts);
    
    lastSnapshotRef.current = previousState;
    
    // Usar setTimeout para garantir que o flag seja resetado após o React processar
    setTimeout(() => {
      isRestoringRef.current = false;
    }, 100);
    
    return true;
  }, [undoStack, createSnapshot, setCards, setActions, setPostIts]);
  
  // Refazer
  const redo = useCallback(() => {
    if (redoStack.length === 0) {
      return false;
    }
    
    isRestoringRef.current = true;
    
    // Salvar estado atual no undo antes de refazer
    const currentSnapshot = createSnapshot('Undo point');
    setUndoStack(prev => [...prev, currentSnapshot]);
    
    // Pegar último estado do redo stack
    const nextState = redoStack[redoStack.length - 1];
    setRedoStack(prev => prev.slice(0, -1));
    
    // Restaurar estado
    setCards(nextState.cards);
    setActions(nextState.actions);
    setPostIts(nextState.postIts);
    
    lastSnapshotRef.current = nextState;
    
    setTimeout(() => {
      isRestoringRef.current = false;
    }, 100);
    
    return true;
  }, [redoStack, createSnapshot, setCards, setActions, setPostIts]);
  
  // Limpar histórico
  const clearHistory = useCallback(() => {
    setUndoStack([]);
    setRedoStack([]);
    lastSnapshotRef.current = null;
  }, []);
  
  // Inicializar com estado atual
  useEffect(() => {
    if (undoStack.length === 0 && currentCards.length > 0) {
      const initialSnapshot = createSnapshot('Estado inicial');
      lastSnapshotRef.current = initialSnapshot;
    }
  }, []);
  
  // Detectar mudanças significativas e salvar automaticamente (com coalescência de bursts)
  // Mudanças em sequência rápida (ex.: arrasto de card) são agrupadas num único snapshot
  // representando o estado ANTES do burst, evitando que o usuário precise clicar Ctrl+Z
  // várias vezes para desfazer uma única ação visual.
  const previousCardsRef = useRef<string>('');
  const previousActionsRef = useRef<string>('');
  const previousPostItsRef = useRef<string>('');
  const pendingBaselineRef = useRef<HistorySnapshot | null>(null);
  const autoSaveTimerRef = useRef<NodeJS.Timeout | null>(null);
  const AUTOSAVE_COALESCE_MS = 400;

  useEffect(() => {
    // Ignorar se estamos restaurando
    if (isRestoringRef.current) return;

    const currentCardsJson = JSON.stringify(currentCards);
    const currentActionsJson = JSON.stringify(currentActions);
    const currentPostItsJson = JSON.stringify(currentPostIts);

    // Primeira renderização: apenas inicializa refs, não há baseline
    const hasPrev = previousCardsRef.current !== '' || previousActionsRef.current !== '' || previousPostItsRef.current !== '';
    const cardsChanged = previousCardsRef.current !== '' && previousCardsRef.current !== currentCardsJson;
    const actionsChanged = previousActionsRef.current !== '' && previousActionsRef.current !== currentActionsJson;
    const postItsChanged = previousPostItsRef.current !== '' && previousPostItsRef.current !== currentPostItsJson;

    if (hasPrev && (cardsChanged || actionsChanged || postItsChanged)) {
      // Se ainda não temos baseline, este é o início de um burst:
      // capturamos o estado ANTERIOR (antes da primeira mudança) como snapshot.
      if (!pendingBaselineRef.current) {
        pendingBaselineRef.current = {
          cards: previousCardsRef.current ? JSON.parse(previousCardsRef.current) : [],
          actions: previousActionsRef.current ? JSON.parse(previousActionsRef.current) : [],
          postIts: previousPostItsRef.current ? JSON.parse(previousPostItsRef.current) : [],
          timestamp: Date.now(),
          description: 'Auto-save'
        };
      }

      // Reinicia o timer de coalescência a cada mudança subsequente.
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
      autoSaveTimerRef.current = setTimeout(() => {
        const baseline = pendingBaselineRef.current;
        pendingBaselineRef.current = null;
        autoSaveTimerRef.current = null;
        if (!baseline) return;
        if (areSnapshotsEqual(lastSnapshotRef.current, baseline)) return;
        setUndoStack(prev => [...prev, baseline].slice(-MAX_HISTORY_SIZE));
        setRedoStack([]);
        lastSnapshotRef.current = baseline;
      }, AUTOSAVE_COALESCE_MS);
    }

    // Atualizar refs com valores atuais
    previousCardsRef.current = currentCardsJson;
    previousActionsRef.current = currentActionsJson;
    previousPostItsRef.current = currentPostItsJson;
  }, [currentCards, currentActions, currentPostIts, areSnapshotsEqual]);
  
  // Cleanup do timer
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, []);
  
  return {
    undo,
    redo,
    pushToHistory,
    pushToHistoryImmediate,
    clearHistory,
    canUndo: undoStack.length > 0,
    canRedo: redoStack.length > 0,
    undoStackSize: undoStack.length,
    redoStackSize: redoStack.length,
    isRestoring: isRestoringRef.current
  };
}
